<template>
  <div class="recur-form">
    <!--  -->
    <div class="recur-form__input">
      <p>{{ $t("recur.starts_on") }}</p>
      <!--  -->
      <DatePicker
        v-model="data.start_date"
        :min-date="new Date()"
        :attributes="attrs"
        @input="change"
        :locale="$i18n.locale"
        :first-day-of-week="1"
        :masks="{ L: 'MMM DD, YYYY' }"
      >
        <template #default="{ togglePopover, inputValue }">
          <p @click="togglePopover()">
            <input
              type="text"
              class="start_on_date"
              :value="inputValue"
              placeholder="MM DD, YYYY"
              @keypress="clearValue"
            />
          </p>
        </template>
      </DatePicker>
    </div>
    <!--  -->
    <div class="recur-form__input">
      <p>{{ $t("recur.repeat.title") }}</p>
      <div class="recur-form__input--select repeat">
        <select
          style="width: 130px"
          @change="change('repeat')"
          v-model="data.repeat"
        >
          <option value="daily">{{ $t("recur.repeat.daily") }}</option>
          <option value="every_weekday">
            {{ $t("recur.repeat.every_weekday") }}
          </option>
          <option value="weekly">{{ $t("recur.repeat.weekly") }}</option>
          <option value="every_two_weeks">
            {{ $t("recur.repeat.every_two_weeks") }}
          </option>
          <option value="monthly">{{ $t("recur.repeat.monthly") }}</option>
          <option value="every_two_months">
            {{ $t("recur.repeat.every_two_months") }}
          </option>
          <option value="yearly">{{ $t("recur.repeat.yearly") }}</option>
        </select>
        <img :src="require(`@/assets/static/images/arrow-select.svg`)" alt="" />
      </div>
    </div>
    <!--  -->
    <div class="recur-form__input">
      <p>{{ $t("recur.show_in_advance.title") }}</p>
      <input
        v-if="data.repeat == 'daily' || data.repeat == 'every_weekday'"
        type="number"
        value="0"
        class="recur-form__input--number show_advance"
        disabled
        v-model="data.show_advance"
      />
      <input
        v-else
        type="number"
        min="0"
        :max="show_in_advance_max"
        value="3"
        class="recur-form__input--number show_advance"
        @change="change"
        @input="checkMax"
        v-model="data.show_advance"
      />
      <p style="margin-left: 5px">{{ $t("recur.show_in_advance.days") }}</p>
    </div>
    <!--  -->
    <div class="recur-form__input" style="min-width: 250px">
      <p>{{ $t("recur.ends.title") }}</p>
      <div class="recur-form__input--select end_type">
        <select @change="onChangeEnds" v-model="data.end_type">
          <option value="never">{{ $t("recur.ends.never") }}</option>
          <option value="after">{{ $t("recur.ends.after") }}</option>
          <option value="on_date">{{ $t("recur.ends.on_date") }}</option>
        </select>
        <img :src="require(`@/assets/static/images/arrow-select.svg`)" alt="" />
      </div>
      <div v-if="show_occurrences" style="margin-left: 15px">
        <DatePicker
          v-model="data.end_date"
          :min-date="new Date()"
          :disabled-dates="
            data.repeat == 'every_weekday' ? { weekdays: [1, 7] } : null
          "
          @input="change"
          :locale="$i18n.locale"
          :first-day-of-week="1"
          :masks="{ L: 'MMM DD, YYYY' }"
        >
          <template #default="{ togglePopover, inputValue }">
            <p @click="togglePopover()">
              <input
                type="text"
                class="ends_on"
                :value="inputValue"
                placeholder="MM DD, YYYY"
                style="width: 120px"
                @keypress="clearValue"
              />
            </p>
          </template>
        </DatePicker>
      </div>
      <div
        v-if="show_after_date"
        style="margin-left: 15px; display: flex; align-items: center"
      >
        <input
          type="number"
          value="1"
          min="1"
          max="100"
          class="recur-form__input--number ends_after"
          @change="change"
          @input="checkMaxOccurences"
          v-model="data.occurrences"
        />
        <p style="margin-left: 5px">{{ $t("recur.occurences") }}</p>
      </div>
    </div>

    <!-- {{ recurNotValid }} -->
    <!--  -->
  </div>
</template>

<script>
import DatePicker from "v-calendar/lib/components/date-picker.umd";

export default {
  name: "Recur",
  props: {
    show_form: Boolean,
  },
  components: {
    DatePicker,
  },
  data() {
    return {
      ends_on: new Date(),
      start_on_date: "",
      show_after_date: false,
      show_occurrences: false,
      show_in_advance_max: 0,
      disable_weekends: false,
      data: {
        start_date: "",
        repeat: "daily",
        show_advance: 0,
        end_type: "never",
        occurrences: 1,
        end_date: "",
      },
      attrs: [
        {
          key: "today",
          highlight: true,
          dates: new Date(),
        },
      ],
    };
  },
  mounted() {
    // The linked-list view in chats does not have the legacy `.project_body`
    // wrapper. Observe the actual responsive container instead.
    const el =
      this.$el.closest(
        ".project, .list_content, .chat-sidebar-container, .tasks"
      ) || this.$el.parentElement;
    const recur_form = this.$el;

    const applyResponsiveLayout = (width) => {
      if (width < 1050) {
        recur_form.classList.add("small");
      } else {
        recur_form.classList.remove("small");
      }
    };

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        applyResponsiveLayout(entry.contentRect.width);
      }
    });

    if (el) {
      observer.observe(el);
      applyResponsiveLayout(el.getBoundingClientRect().width);
    } else {
      applyResponsiveLayout(this.$el.getBoundingClientRect().width);
    }

    this._recurResizeObserver = observer;

    document.addEventListener("keydown", this.onKeyDown);
  },
  methods: {
    onKeyDown(event) {
      if (this.show_form) {
        if (event.key === "Enter") {
          let valid = [];

          if (this.data.end_type == "on_date" && isNaN(this.data.end_date)) {
            valid.push(true);
            document
              .querySelector(".recur-form__input .ends_on")
              .classList.add("error");
          } else {
            if (document.querySelector(".recur-form__input .ends_on")) {
              document
                .querySelector(".recur-form__input .ends_on")
                .classList.remove("error");
            }
          }

          if (this.data.end_type == "after" && this.data.occurrences <= 0) {
            valid.push(true);
            document
              .querySelector(".recur-form__input .ends_after")
              .classList.add("error");
          } else {
            if (document.querySelector(".recur-form__input .ends_after")) {
              document
                .querySelector(".recur-form__input .ends_after")
                .classList.remove("error");
            }
          }

          if (this.data.start_date == "" || isNaN(this.data.start_date)) {
            valid.push(true);
            document
              .querySelector(".recur-form__input .start_on_date")
              .classList.add("error");
          } else {
            if (document.querySelector(".recur-form__input .start_on_date")) {
              document
                .querySelector(".recur-form__input .start_on_date")
                .classList.remove("error");
            }
          }

          if (valid.length > 0) return;

          this.$emit("keyPress:enter");
          this.defaultData();
        }
      }
    },
    checkMax(input) {
      input = input.target;
      if (Number(input.value) > Number(input.max)) {
        this.data.show_advance = input.max;
      }
    },
    change(e) {
      if (e == "repeat") {
        this.disable_weekends = false;
        if (
          this.data.repeat == "daily" ||
          this.data.repeat == "every_weekday"
        ) {
          this.data.show_advance = 0;
        } else {
          this.data.show_advance = 3;
        }
        if (
          this.data.repeat == "weekly" ||
          this.data.repeat == "every_two_weeks"
        ) {
          this.show_in_advance_max = 3;
        }
        if (
          this.data.repeat == "monthly" ||
          this.data.repeat == "every_two_months"
        ) {
          this.show_in_advance_max = 7;
        }
        if (this.data.repeat == "yearly") {
          this.show_in_advance_max = 30;
        }
      }
      this.data.start_date = new Date(this.data.start_date).getTime();
      this.data.end_date = new Date(this.data.end_date).getTime();
      this.$emit("change", this.data);
    },
    onChangeEnds(event) {
      if (event.target.value === "on_date") {
        this.show_occurrences = true;
      } else {
        this.show_occurrences = false;
      }

      if (event.target.value === "after") {
        this.show_after_date = true;
      } else {
        this.show_after_date = false;
      }

      this.change();
    },
    clearValue(event) {
      setTimeout(() => (event.target.value = ""), 5);
    },
    checkMaxOccurences(input) {
      input = input.target;
      if (Number(input.value) < Number(input.min)) {
        this.data.occurrences = input.min;
      }
      if (Number(input.value) > Number(input.max)) {
        this.data.occurrences = input.max;
      }
    },
    defaultData() {
      setTimeout(() => {
        (this.ends_on = ""),
          (this.start_on_date = ""),
          (this.show_after_date = false),
          (this.show_occurrences = false),
          (this.data = {
            start_date: "",
            repeat: "daily",
            show_advance: 0,
            end_type: "never",
            occurrences: 1,
            end_date: "",
          });
      }, 500);
    },
  },
  beforeDestroy() {
    if (this._recurResizeObserver) {
      this._recurResizeObserver.disconnect();
    }
    document.removeEventListener("keydown", this.onKeyDown);
  },
  watch: {
    start_on_date(value) {
      if (value) {
        this.attrs = [
          {
            key: "selected",
            highlight: { backgroundColor: "#42b983" },
            dates: new Date(value),
          },
        ];
      } else {
        this.attrs = [{ key: "today", highlight: true, dates: new Date() }];
      }
    },
    $route(to) {
      this.defaultData();
    },
  },
  computed: {
    recurNotValid() {
      return this.$store.state.tasks.recurNotValid.forEach((e) => {
        document.querySelector(".start_on_date").classList.remove("error");
        document
          .querySelector(".recur-form__input--select.repeat")
          .classList.remove("error");
        document.querySelector(".show_advance").classList.remove("error");
        document.querySelector(".end_type").classList.remove("error");
        document.querySelector(".ends_after").classList.remove("error");
        document.querySelector(".ends_on").classList.remove("error");

        if (e == "start_date") {
          document.querySelector(".start_on_date").classList.add("error");
        }
        if (e == "repeat") {
          document
            .querySelector(".recur-form__input--select.repeat")
            .classList.add("error");
        }
        if (e == "show_advance") {
          document.querySelector(".show_advance").classList.add("error");
        }
        if (e == "end_type") {
          document.querySelector(".end_type").classList.add("error");
        }
        if (e == "occurrences") {
          document.querySelector(".ends_after").classList.add("error");
        }
        if (e == "end_date") {
          document.querySelector(".ends_on").classList.add("error");
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.recur-form {
  display: flex;
  justify-content: flex-end;
  padding-bottom: 30px;
  &.small {
    flex-direction: column;
    align-items: flex-end;
    .recur-form__input {
      width: 300px !important;
      margin-bottom: 10px;
      padding: 12px 20px;
    }
  }
  &__input {
    display: flex;
    align-items: center;
    padding: 16px 27px;
    margin-left: 10px;
    background-color: rgba(249, 249, 249, 0.7);
    border-radius: 2px;
    border-radius: 6px;

    &:nth-child(1) {
      animation: t_recur_form 0.1s ease-in-out;
    }
    &:nth-child(2) {
      animation: t_recur_form 0.2s ease-in-out;
    }
    &:nth-child(3) {
      animation: t_recur_form 0.3s ease-in-out;
    }
    &:nth-child(4) {
      animation: t_recur_form 0.4s ease-in-out;
    }

    @keyframes t_recur_form {
      0% {
        opacity: 0;
        transform: translateY(-10px);
      }
      100% {
        opacity: 1;
        transform: translateY(0px);
      }
    }

    &:first-child {
      margin-left: 0px;
    }
    p {
      margin-right: 10px;
      font-size: 13px;
      color: #464646;
    }
    input[type="text"] {
      border: 0px;
      width: 100px;
      height: 30px;
      outline: none;
      font-size: 10px;
      color: #464646;
      text-align: center;
      border: 1px solid #efefef;
      border-radius: 6px;
      &::placeholder {
        font-size: 10px;
        color: rgba(70, 70, 70, 0.506);
      }
    }
    &--select {
      position: relative;
      select {
        border-radius: 6px;
        font-size: 10px;
        color: #464646;
        // width: 150px;
        height: 32px;
        // border-radius: 2px;
        outline: none;
        padding: 0 20px 0 13px;
        background-color: #ffffff;
        border: 1px solid #efefef;
        -webkit-appearance: none;
      }
      img {
        position: absolute;
        right: 10px;
        top: 14px;
      }
    }
    &--number {
      width: 30px;
      text-align: center;
      padding: 0px 3px;
      border: 0px;
      height: 30px;
      outline: none;
      font-size: 10px;
      color: #464646;
      border-radius: 6px;
      border: 1px solid #efefef;
      -moz-appearance: textfield;
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
      }
      &::placeholder {
        font-size: 10px;
        color: rgba(70, 70, 70, 0.506);
      }
    }
    .error {
      border: 1px solid red !important;
    }
  }
}
</style>
